import React, { useState, useEffect, useRef } from 'react';
import './Element.css';
import { useSelector, useDispatch } from "react-redux";
import { updateGoToSlide, updateElementInputFocus } from '../../redux/slices/userActivity';
import { useLocation } from "react-router-dom";
import { showDuonutresponse, saveQuizAnsAsync, saveQuizResponse, showduonutData } from '../../redux/slices/DuonutReducer';
import axios from "axios";
import { domain } from "../../services/constants";
import useWindowDimensions from '../../hooks/useWindowDimensions';

const Checkbox = ({
    id,
    elementAttr: {
        text, color, fontFamily, fontSize, fontWeight, options, fontStyle, textDecoration, textTransform, textAlign,
        lineHeight, letterSpacing, backgroundColor, border, margin, padding, borderRadius, other, maxLength, gap,
        labelFontSize, labelFontWeight, labelColor, required, fieldPosition,
    },
    elementHoverAttr,
    language,
    slideId,
}) => {
    const dispatch = useDispatch();
    const { selectedSlide, queSpeechPlaying } = useSelector((state) => state.userActivity);
    const location = useLocation();
    const save_ans_response = useSelector(showDuonutresponse);
    const { viewer_display_id, viewer_nick_name, viewer_url_params } = useSelector((state) => state.duonut);
    const [selectedAnswer, setSelectedAnswer] = useState([]);
    const [otherText, setOtherText] = useState('');
    const [hover, setHover] = useState(-1);
    const [inputText, setInputText] = useState("");
    const [optionsText, setOptionsText] = useState([]);
    const duonutDataMain = useSelector(showduonutData);
    const duonutData = duonutDataMain[0];
    const { viewType } = useSelector((state) => state.duonutSettings);
    const { width } = useWindowDimensions();
    const flexDirection = (viewType === 3 || width < 800) ? "column" : fieldPosition;

    const checkboxRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [typedText, setTypedText] = useState("");
    const [isTypingDone, setIsTypingDone] = useState(true);

    useEffect(() => {
        if (!inputText || !audioUrl) return;

        // Safely split words including spaces
        const words = inputText.match(/\S+\s*/g)?.filter(Boolean) || [];

        if (words.length === 0) return;

        let index = 0;
        let currentText = "";
        setIsTypingDone(false); // start typing → disable input

        const interval = setInterval(() => {
            if (index < words.length) {
                currentText += words[index]; // safe accumulation
                setTypedText(currentText);
                index++;
            } else {
                clearInterval(interval);
                setIsTypingDone(true); // ✅ Typing complete → enable input
            }
        }, 300);

        return () => clearInterval(interval);
    }, [inputText, audioUrl]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsVisible(entry.isIntersecting);
            },
            { threshold: 0.5 } // 50% of the div must be visible
        );

        if (checkboxRef.current) {
            observer.observe(checkboxRef.current);
        }

        return () => {
            if (checkboxRef.current) {
                observer.unobserve(checkboxRef.current);
            }
        };
    }, [inputText]);

    useEffect(() => {
        if (isVisible && inputText && queSpeechPlaying && duonutData?.setting?.question_speech && location.pathname.includes("/duonut/") && slideId === `slide-${selectedSlide}`) {
            fetchAudio(inputText);
        } else {
            setAudioUrl(null);
        }
    }, [isVisible, inputText, queSpeechPlaying]);

    useEffect(() => {
        setSelectedAnswer([]);
        if (location.pathname.includes("/duonut/") && slideId === `slide-${selectedSlide}` && language !== "en") {
            fetchText(text);
            fetchOptionsText(options);
        } else {
            setInputText(text);
            setOptionsText(options);
        }
    }, [text, options]);

    const fetchOptionsText = async (texts) => {
        const translatedTexts = [];
        const delimiter = "[[SEP]]";
        const delimiterRegex = /\[\[SEP\]\]|\[SEP\]/;
        const textToTranslate = texts.join(delimiter);
        const translated = await translateText(textToTranslate, language);
        const translatedArray = translated.split(delimiterRegex);
        translatedTexts.push(...translatedArray);
        setOptionsText(translatedTexts);
    };

    const fetchText = async (texts) => {
        const t_text = await translateText(texts, language);
        setInputText(t_text);
    };

    const translateText = async (texts, targetLanguage) => {
        try {
            const response = await axios({
                method: "POST",
                url: `${domain}/duonut/language_translator`,
                headers: {
                    "Content-Type": "application/json",
                },
                data: {
                    source: "en",
                    target: targetLanguage,
                    query: texts,
                },
            });

            if (response.status === 200) {
                return response?.data.translation;
            } else {
                return "Error: Translation failed. Please try again.";
            }
        } catch (error) {
            console.error('Error in translation API:', error);
            return "Error translating text. Please try again.";
        }
    };

    const fetchAudio = async (texts) => {
        const response = await fetch(`${domain}/duonut/get_speech`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ question_text: texts }),
        });

        const data = await response.json();
        if (data.audio_base64) {
            const audioSrc = `data:audio/mp3;base64,${data.audio_base64}`;
            setAudioUrl(audioSrc);
        }
    };

    const handleAnswerChange = (item) => {
        let selectedAnswerCopy = [...selectedAnswer];
        if (!selectedAnswerCopy.includes(item)) {
            selectedAnswerCopy.push(item);
        } else {
            selectedAnswerCopy = selectedAnswerCopy.filter(answer => answer !== item);
        }
        setSelectedAnswer(selectedAnswerCopy);
        submitResponse(selectedAnswerCopy);
    };

    const submitResponse = (selectedAnswers) => {

        const ans_other = selectedAnswers.map(selectedans =>
            selectedans === "other" ? `${selectedans} - ${otherText}` : selectedans
        );

        let ans = ans_other.join("; ");

        if (location.pathname.includes("/duonut/")) {
            var save_ans = {
                display_id: viewer_display_id,
                watch_id: save_ans_response[0] ? save_ans_response[0].watch_id : "",
                result: 0,
                question: text ? text : "",
                answered_text: ans,
                nickname: viewer_nick_name,
                step: selectedSlide + 1,
                type: "CheckboxGroup",
                url_params: viewer_url_params,
            };

            const formData = new FormData();
            Object.keys(save_ans).forEach(key => {
                const value = typeof save_ans[key] === "object" ? JSON.stringify(save_ans[key]) : save_ans[key];
                formData.append(key, value);
            });

            dispatch(saveQuizAnsAsync(formData));

            var save_response = {
                id: id,
                type: "CheckboxGroup",
                step: selectedSlide + 1,
                question: text ? text : "",
                answered_text: ans,
            };
            dispatch(saveQuizResponse(save_response));
        }
    };

    const handleOtherInputChange = (e) => {
        if (location.pathname.includes("/duonut/")) {
            setOtherText(e.target.value);
            submitResponse(selectedAnswer);
        }
    };

    const handleInputFocus = () => {
        dispatch(updateElementInputFocus(true));
    };

    const handleInputBlur = () => {
        dispatch(updateElementInputFocus(false));
    };

    return (
        <div ref={checkboxRef} >
            {(audioUrl || !duonutData?.setting?.question_speech || !queSpeechPlaying) ?
                <>
                    {audioUrl && (
                        <audio autoPlay style={{ display: "none" }}>
                            <source src={audioUrl} type="audio/mp3" />
                            Your browser does not support the audio element.
                        </audio>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: gap }}>
                        <div style={{
                            width: "100%", paddingBottom: '10px',
                            color: labelColor, fontFamily, fontSize: labelFontSize, fontWeight: labelFontWeight, fontStyle, textDecoration, textTransform,
                            textAlign, lineHeight, letterSpacing
                        }}>{(duonutData?.setting?.question_speech && queSpeechPlaying && location.pathname.includes("/duonut/")) ? typedText : inputText}</div>
                        <div style={{
                            display: "flex",
                            justifyContent:
                                fieldPosition === "row"
                                    ? "space-around"
                                    : textAlign === "justify"
                                        ? "flex-start"
                                        : textAlign,
                            alignItems: 'flex-start',
                            textAlign,
                            flexDirection
                        }}>
                            {isTypingDone && <>
                                {options.map((item, i) => {
                                    return <label style={{
                                        display: "flex", justifyContent: "flex-start", alignItems: "center", lineHeight, width: flexDirection === "column" ? "100%" : "auto",
                                    }} key={i} htmlFor={`checkbox_option${slideId}_${id}_${i}`}
                                        onMouseEnter={() => setHover(i)}
                                        onMouseLeave={() => setHover(-1)} >
                                        <input type="checkbox" id={`checkbox_option${slideId}_${id}_${i}`} name={`checkbox_option${slideId}_${id}_${i}`}
                                            style={{
                                                width: fontSize, height: fontSize, minWidth: fontSize, minHeight: fontSize, margin: "2px",
                                                //  accentColor: `${color}`
                                            }}
                                            checked={selectedAnswer.includes(item) ? true : false}
                                            disabled={(maxLength !== 0 && maxLength === selectedAnswer.length && !selectedAnswer.includes(item)) ? true : false}
                                            onChange={() => handleAnswerChange(item)}
                                        />
                                        <div style={{
                                            backgroundColor: hover === i ? elementHoverAttr?.backgroundColor : backgroundColor,
                                            border: hover === i ? elementHoverAttr?.border : border ? border : "0",
                                            color: hover === i ? elementHoverAttr?.color : color,
                                            borderRadius, fontFamily, fontSize, fontWeight, fontStyle, textDecoration,
                                            textTransform, textAlign, letterSpacing, padding, margin,
                                            cursor: (location.pathname).includes("/duonut/") ? "pointer" : "default",
                                            width: "100%", height: "100%"
                                        }}
                                        // onClick={() => handleAnswerChange(item)}
                                        >
                                            <div>{optionsText[i]}</div>
                                        </div>
                                    </label>
                                })}
                                {other && <div style={{
                                    display: "flex", justifyContent: "flex-start", alignItems: "center", lineHeight, width: "100%"
                                }}>
                                    <input type="checkbox" id={`checkbox_option_other${slideId}_${id}`} name={`checkbox_option_other${slideId}_${id}`}
                                        style={{
                                            width: fontSize, height: fontSize, minWidth: fontSize, minHeight: fontSize, margin: "2px",
                                            // accentColor: `${color}`
                                        }}
                                        checked={selectedAnswer.includes("other") ? true : false}
                                        disabled={(maxLength !== 0 && maxLength === selectedAnswer.length && !selectedAnswer.includes("other")) ? true : false}
                                        onChange={() => handleAnswerChange("other")}
                                    />
                                    <div style={{
                                        backgroundColor: hover === options.length ? elementHoverAttr?.backgroundColor : backgroundColor,
                                        border: hover === options.length ? elementHoverAttr?.border : border ? border : "0",
                                        color: hover === options.length ? elementHoverAttr?.color : color,
                                        borderRadius, fontFamily, fontSize, fontWeight, fontStyle, textDecoration,
                                        textTransform, textAlign, letterSpacing, padding, margin,
                                        cursor: (location.pathname).includes("/duonut/") ? "pointer" : "default",
                                        width: "100%", height: "100%"
                                    }}
                                        onMouseEnter={() => setHover(options.length)}
                                        onMouseLeave={() => setHover(-1)}
                                        onClick={() => handleAnswerChange("other")}>
                                        <div>{language === "hi" ? "अन्य" :
                                            language === "es" ? "Otra" :
                                                language === "ja" ? "他の" :
                                                    language === "ar" ? "آخر" :
                                                        language === "fr" ? "Autre" :
                                                            language === "nl" ? "Ander" :
                                                                language === "pt" ? "Outra" : "Other"}</div>
                                    </div>
                                    {(location.pathname.includes("/duonut/") && selectedAnswer.includes("other")) &&
                                        <>
                                            <input type='text' style={{
                                                minWidth: "2rem", margin: "0px 6px", padding: "10px", width: "100%",
                                                color, fontFamily, fontSize: fontSize, fontWeight, fontStyle, textDecoration, textTransform,
                                                textAlign, lineHeight, letterSpacing,
                                                backgroundColor, borderBottom: "1px solid gray", borderRadius,
                                            }}
                                                onChange={(e) => handleOtherInputChange(e)}
                                                readOnly={(location.pathname.includes("/duonut/") && selectedAnswer.includes("other")) ? false : true}
                                            />
                                            <button onClick={() => submitResponse(selectedAnswer)}
                                                style={{ backgroundColor: "#4299e1", color: "white", borderRadius: "6px", padding: '1rem', cursor: "pointer" }}>
                                                <i className="fa fa-arrow-right" aria-hidden="true"></i></button>
                                        </>
                                    }
                                </div>}
                            </>}
                        </div>
                    </div>
                </> : <div style={{ display: "flex", justifyContent: "center" }}><img alt="loader" src={require(`../../assets/Images/dot_loading.gif`)} /></div>}
        </div>
    );
}

export default Checkbox;